import type React from "react";
import { useCallback, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { isUserAdmin } from "../types/user";
import DedupeEntityPanel from "../components/dedupe/DedupeEntityPanel";
import {
  applyCityMergeApi,
  applyTruckMergeApi,
  applyUserMergeApi,
  dryRunCityMergeApi,
  dryRunTruckMergeApi,
  dryRunUserMergeApi,
  fetchCityDedupeCandidatesApi,
  fetchTruckDedupeCandidatesApi,
  fetchUserDedupeCandidatesApi,
  type CityMergeFields,
  type DedupeCity,
  type DedupeTruck,
  type DedupeUser,
  type TruckMergeFields,
  type UserMergeFields,
} from "../api/dtoolsApi";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;
type DedupeTab = "users" | "cities" | "trucks";

const DedupeEntitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const canMutate = isUserAdmin(user);
  const [tab, setTab] = useState<DedupeTab>("users");

  const loadUsers = useCallback(async () => {
    const result = await fetchUserDedupeCandidatesApi();
    return {
      groups: result.groups.map((group) => ({
        score: group.score,
        items: group.users,
      })),
      allItems: result.allUsers,
    };
  }, []);

  const loadCities = useCallback(async () => {
    const result = await fetchCityDedupeCandidatesApi();
    return {
      groups: result.groups.map((group) => ({
        score: group.score,
        items: group.cities,
      })),
      allItems: result.allCities,
    };
  }, []);

  const loadTrucks = useCallback(async () => {
    const result = await fetchTruckDedupeCandidatesApi();
    return {
      groups: result.groups.map((group) => ({
        score: group.score,
        items: group.trucks,
      })),
      allItems: result.allTrucks,
    };
  }, []);

  return (
    <Box sx={{ mt: 2, pb: 6 }}>
      <Typography variant="h4" gutterBottom>
        {t("Deduplicate Entities" as TranslationKeys)}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        {t(
          "Find likely duplicates, choose which record to keep, pick final field values, then dry-run before applying. References are rewritten and the discarded entity is deleted." as TranslationKeys,
        )}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_event, value: DedupeTab) => setTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("Users" as TranslationKeys)} value="users" />
        <Tab label={t("Locations" as TranslationKeys)} value="cities" />
        <Tab label={t("Trucks" as TranslationKeys)} value="trucks" />
      </Tabs>

      {tab === "users" && (
        <DedupeEntityPanel<DedupeUser, UserMergeFields>
          readOnly={!canMutate}
          entityLabel={t("Users" as TranslationKeys)}
          loadCandidates={loadUsers}
          getOptionLabel={(entity) => `${entity._id} — ${entity.name || "(no name)"}`}
          getChipLabel={(entity) => `${entity._id} · ${entity.name || "—"}`}
          fields={[
            { key: "name", label: "Name", getValue: (e) => e.name },
            { key: "role", label: "Role", getValue: (e) => e.role },
            { key: "timezone", label: "Timezone", getValue: (e) => e.timezone },
            { key: "deactivated", label: "Deactivated", getValue: (e) => e.deactivated },
            { key: "isTempPassword", label: "Temp Password", getValue: (e) => e.isTempPassword },
            {
              key: "initiallyCreatedFrom",
              label: "Created From",
              getValue: (e) => e.initiallyCreatedFrom,
            },
            {
              key: "initialLogislyRef",
              label: "Logisly Ref",
              getValue: (e) => e.initialLogislyRef,
            },
          ]}
          fieldsFromEntity={(entity) => ({
            name: entity.name,
            role: entity.role,
            timezone: entity.timezone,
            deactivated: entity.deactivated,
            isTempPassword: entity.isTempPassword,
            initiallyCreatedFrom: entity.initiallyCreatedFrom,
            initialLogislyRef: entity.initialLogislyRef,
          })}
          identityNote={t(
            "Username (_id) always stays as the keep user. Password hash is never merged from the discarded user." as TranslationKeys,
          )}
          dryRun={async ({ keepId, discardId, fields }) => {
            const result = await dryRunUserMergeApi({
              keepUserId: keepId,
              discardUserId: discardId,
              fields,
            });
            return {
              dryRunToken: result.dryRunToken,
              plan: {
                willDeleteId: result.plan.willDeleteUserId,
                referenceUpdates: result.plan.referenceUpdates,
                extraSummary: [
                  {
                    label: t("Refresh tokens to delete" as TranslationKeys),
                    value: result.plan.refreshTokensToDelete,
                  },
                ],
              },
            };
          }}
          apply={async ({ keepId, discardId, fields, dryRunToken }) => {
            await applyUserMergeApi({
              keepUserId: keepId,
              discardUserId: discardId,
              fields,
              dryRunToken,
            });
          }}
          successMessage={t("Users merged successfully" as TranslationKeys)}
        />
      )}

      {tab === "cities" && (
        <DedupeEntityPanel<DedupeCity, CityMergeFields>
          readOnly={!canMutate}
          entityLabel={t("Locations" as TranslationKeys)}
          loadCandidates={loadCities}
          getOptionLabel={(entity) => `${entity.name} (${entity._id})`}
          getChipLabel={(entity) => entity.name}
          fields={[
            { key: "name", label: "Name", getValue: (e) => e.name },
            {
              key: "initiallyCreatedFrom",
              label: "Created From",
              getValue: (e) => e.initiallyCreatedFrom,
            },
            {
              key: "initialLogislyRef",
              label: "Logisly Ref",
              getValue: (e) => e.initialLogislyRef,
            },
          ]}
          fieldsFromEntity={(entity) => ({
            name: entity.name,
            initiallyCreatedFrom: entity.initiallyCreatedFrom,
            initialLogislyRef: entity.initialLogislyRef,
          })}
          identityNote={t(
            "City id always stays as the keep city. Order routes, truck locations, and Logisly links are rewritten." as TranslationKeys,
          )}
          dryRun={async ({ keepId, discardId, fields }) => {
            const result = await dryRunCityMergeApi({
              keepCityId: keepId,
              discardCityId: discardId,
              fields,
            });
            return {
              dryRunToken: result.dryRunToken,
              plan: {
                willDeleteId: result.plan.willDeleteCityId,
                referenceUpdates: result.plan.referenceUpdates,
              },
            };
          }}
          apply={async ({ keepId, discardId, fields, dryRunToken }) => {
            await applyCityMergeApi({
              keepCityId: keepId,
              discardCityId: discardId,
              fields,
              dryRunToken,
            });
          }}
          successMessage={t("Cities merged successfully" as TranslationKeys)}
        />
      )}

      {tab === "trucks" && (
        <DedupeEntityPanel<DedupeTruck, TruckMergeFields>
          readOnly={!canMutate}
          entityLabel={t("Trucks" as TranslationKeys)}
          loadCandidates={loadTrucks}
          getOptionLabel={(entity) =>
            `${entity.licensePlate} · ${entity.type}${entity.retired ? " (retired)" : ""}`
          }
          getChipLabel={(entity) => entity.licensePlate}
          fields={[
            { key: "licensePlate", label: "License Plate", getValue: (e) => e.licensePlate },
            { key: "type", label: "Type", getValue: (e) => e.type },
            { key: "retired", label: "Retired", getValue: (e) => e.retired },
            {
              key: "suggestedDriverId",
              label: "Suggested Driver",
              getValue: (e) => e.suggestedDriverId,
            },
            {
              key: "initiallyCreatedFrom",
              label: "Created From",
              getValue: (e) => e.initiallyCreatedFrom,
            },
            {
              key: "initialLogislyRef",
              label: "Logisly Ref",
              getValue: (e) => e.initialLogislyRef,
            },
          ]}
          fieldsFromEntity={(entity) => ({
            licensePlate: entity.licensePlate,
            type: entity.type,
            retired: entity.retired,
            suggestedDriverId: entity.suggestedDriverId,
            initiallyCreatedFrom: entity.initiallyCreatedFrom,
            initialLogislyRef: entity.initialLogislyRef,
          })}
          identityNote={t(
            "Truck id always stays as the keep truck. Orders, histories, groups, and Logisly links are rewritten." as TranslationKeys,
          )}
          dryRun={async ({ keepId, discardId, fields }) => {
            const result = await dryRunTruckMergeApi({
              keepTruckId: keepId,
              discardTruckId: discardId,
              fields,
            });
            return {
              dryRunToken: result.dryRunToken,
              plan: {
                willDeleteId: result.plan.willDeleteTruckId,
                referenceUpdates: result.plan.referenceUpdates,
              },
            };
          }}
          apply={async ({ keepId, discardId, fields, dryRunToken }) => {
            await applyTruckMergeApi({
              keepTruckId: keepId,
              discardTruckId: discardId,
              fields,
              dryRunToken,
            });
          }}
          successMessage={t("Trucks merged successfully" as TranslationKeys)}
        />
      )}
    </Box>
  );
};

export default DedupeEntitiesPage;
